/**
 * Normalize product description + key features for the MatchHeader popover.
 * Strips HTML safely (text only), dedupes features, never returns raw markup.
 */

function decodeEntities(text) {
  return String(text || "")
    .replace(/&nbsp;/gi, " ")
    .replace(/&amp;/gi, "&")
    .replace(/&lt;/gi, "<")
    .replace(/&gt;/gi, ">")
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/gi, "'")
    .replace(/&#x27;/gi, "'")
    .replace(/&#(\d+);/g, (_, n) => {
      const code = Number(n);
      return Number.isFinite(code) ? String.fromCharCode(code) : "";
    });
}

function stripTags(html) {
  return decodeEntities(
    String(html || "")
      .replace(/<script[\s\S]*?<\/script>/gi, " ")
      .replace(/<style[\s\S]*?<\/style>/gi, " ")
      .replace(/<br\s*\/?>/gi, "\n")
      .replace(/<\/(p|div|h[1-6]|li|tr)>/gi, "\n")
      .replace(/<[^>]+>/g, " "),
  );
}

function cleanLine(value) {
  return stripTags(value).replace(/\s+/g, " ").trim();
}

/**
 * @param {unknown} rawDescription
 * @param {unknown[]} [keyFeatures]
 * @returns {{ description: string, features: string[] }}
 */
export function normalizeProductDescription(rawDescription, keyFeatures = []) {
  const features = [];
  const seen = new Set();

  const addFeature = (value) => {
    const line = cleanLine(value);
    if (!line) return;
    const key = line.toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    features.push(line);
  };

  if (Array.isArray(keyFeatures)) {
    keyFeatures.forEach(addFeature);
  }

  const raw = rawDescription == null ? "" : String(rawDescription);
  // Prefer explicit list items from HTML before flattening prose.
  const liMatches = [...raw.matchAll(/<li\b[^>]*>([\s\S]*?)<\/li>/gi)];
  liMatches.forEach((m) => addFeature(m[1]));

  let description = stripTags(raw)
    .replace(/[ \t]+\n/g, "\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();

  // Drop description paragraphs that duplicate a feature line.
  if (description && features.length) {
    const featureKeys = new Set(features.map((f) => f.toLowerCase()));
    description = description
      .split(/\n+/)
      .map((p) => p.trim())
      .filter((p) => p && !featureKeys.has(p.toLowerCase()))
      .join("\n\n")
      .trim();
  }

  // If the entire description is only one of the features, clear it.
  if (description && features.some((f) => f.toLowerCase() === description.toLowerCase())) {
    description = "";
  }

  return { description, features };
}

export function hasProductDescriptionContent(rawDescription, keyFeatures = []) {
  const { description, features } = normalizeProductDescription(rawDescription, keyFeatures);
  return Boolean(description) || features.length > 0;
}
